/**
 * api-keys-tab.tsx — "API key" của tổ chức (Cài đặt hệ thống).
 *
 * Khoá thuộc về TỔ CHỨC (bảng api_keys theo org_id), không phải của riêng ai — backend (api-key-routes.ts) đòi
 * `api_keys.view` (xem) / `api_keys.create` (tạo) / `api_keys.delete` (thu hồi); thiếu quyền nào thì ẩn nút đó.
 * Khoá mang ĐÚNG quyền của người tạo (không phải quyền quản trị), có thời hạn (mặc định 90 ngày) và mọi thao tác
 * qua khoá ghi dưới tên người tạo.
 * Đổi mật khẩu cá nhân đã chuyển sang Cài đặt cá nhân (/account/profile).
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Copy, KeyRound, Plus, Trash2 } from 'lucide-react'
import { currentLocale } from '@/lib/i18n'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Switch } from '@/components/ui/misc'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loading, EmptyState } from '@/components/shared/feedback'
import {
  useApiKeys, useCreateApiKey, useDeleteApiKey, useUpdateApiKey, useMyPermissions,
} from '@/hooks/use-settings'
import { apiError } from '@/lib/api-client'
import { API_KEY_TTL_OPTIONS, DEFAULT_API_KEY_TTL, apiKeyStatus, ttlDaysOf } from '@/lib/api-keys'

export function ApiKeysTab() {
  const { data: keys, isLoading } = useApiKeys()
  const createKey = useCreateApiKey()
  const updateKey = useUpdateApiKey()
  const deleteKey = useDeleteApiKey()
  const { data: perms } = useMyPermissions()
  const { t } = useTranslation('settings')
  const coQuyenTao = !!perms?.has('api_keys.create')
  const coQuyenThuHoi = !!perms?.has('api_keys.delete')
  const [name, setName] = useState('')
  const [ttl, setTtl] = useState(DEFAULT_API_KEY_TTL)
  const [writeEnabled, setWriteEnabled] = useState(false)
  const [sendEnabled, setSendEnabled] = useState(false)
  const [deleteEnabled, setDeleteEnabled] = useState(false)
  // Khoá thật chỉ trả về ĐÚNG MỘT LẦN lúc tạo — giữ trên màn để người dùng chép.
  const [freshKey, setFreshKey] = useState<string | null>(null)

  const create = async () => {
    if (!name.trim()) return toast.error(t('settings:apiKeys.nameRequired'))
    try {
      const res = await createKey.mutateAsync({ name: name.trim(), expiresInDays: ttlDaysOf(ttl), writeEnabled, sendEnabled, deleteEnabled })
      setFreshKey(res.apiKey)
      setName('')
      setWriteEnabled(false)
      setSendEnabled(false)
      setDeleteEnabled(false)
      toast.success(t('settings:apiKeys.created'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  // Ba cờ MCP TÁCH BIỆT: ghi (huấn luyện / nháp), gửi tin thật + chiến dịch, xoá dữ liệu.
  const toggleCap = async (id: string, cap: 'write' | 'send' | 'delete', v: boolean) => {
    try {
      await updateKey.mutateAsync({ id, [`${cap}Enabled`]: v })
      toast.success(t(`settings:apiKeys.${cap}Toggled${v ? 'On' : 'Off'}`))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const remove = async (id: string, label: string) => {
    if (!window.confirm(t('settings:apiKeys.confirmRevoke', { label }))) return
    try {
      await deleteKey.mutateAsync(id)
      toast.success(t('settings:apiKeys.revoked'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <KeyRound className="h-4 w-4 text-primary" /> API key
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t('settings:apiKeys.description')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {coQuyenTao && (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('settings:apiKeys.namePlaceholder')}
                className="max-w-xs"
              />
              <Select value={ttl} onValueChange={setTtl}>
                <SelectTrigger className="h-9 w-56" aria-label={t('settings:apiKeys.ttlLabel')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {API_KEY_TTL_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{t('settings:apiKeys.expiresAfter', { ttl: o.label })}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={create} disabled={createKey.isPending}>
                <Plus className="h-4 w-4" /> {t('settings:apiKeys.create')}
              </Button>
            </div>
            {([
              ['write', writeEnabled, setWriteEnabled],
              ['send', sendEnabled, setSendEnabled],
              ['delete', deleteEnabled, setDeleteEnabled],
            ] as const).map(([cap, on, setOn]) => (
              <div key={cap} className="flex items-start gap-2 text-sm">
                <Checkbox id={`api-key-${cap}`} className="mt-0.5" checked={on} onCheckedChange={(v) => setOn(v === true)} />
                <label htmlFor={`api-key-${cap}`} className="min-w-0 text-muted-foreground">
                  {t(`settings:apiKeys.${cap}EnabledLabel`)}
                  <span className="block text-xs">{t(`settings:apiKeys.${cap}EnabledHint`)}</span>
                </label>
              </div>
            ))}
          </div>
        )}

        {freshKey && (
          <div className="rounded-lg border border-warning/40 bg-warning/10 p-3">
            <p className="text-xs font-medium">{t('settings:apiKeys.copyNowHint')}</p>
            <div className="mt-1.5 flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1 text-xs">{freshKey}</code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(freshKey).then(
                    () => toast.success(t('settings:apiKeys.copied')),
                    () => toast.error(t('settings:apiKeys.clipboardBlocked')),
                  )
                }}
              >
                <Copy className="h-3.5 w-3.5" /> {t('common:actions.copy')}
              </Button>
            </div>
          </div>
        )}

        {isLoading ? (
          <Loading label={t('settings:apiKeys.loading')} />
        ) : !keys?.length ? (
          <EmptyState
            icon={KeyRound}
            title={t('settings:apiKeys.empty')}
            description={coQuyenTao ? t('settings:apiKeys.emptyHint') : undefined}
          />
        ) : (
          <ul className="divide-y rounded-lg border">
            {keys.map((k) => {
              const st = apiKeyStatus(k)
              const dead = st.state === 'revoked' || st.state === 'expired'
              return (
                <li key={k.id} className={`flex items-center gap-3 px-3 py-2.5 ${dead ? 'opacity-60' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 truncate text-sm font-medium">
                      <span className="truncate">{k.name}</span>
                      {st.state === 'revoked' && <Badge variant="secondary">{t('settings:apiKeys.status.revoked')}</Badge>}
                      {st.state === 'expired' && <Badge variant="secondary">{t('settings:apiKeys.status.expired')}</Badge>}
                      {st.state === 'expiring' && <Badge variant="warning">{t('settings:apiKeys.status.expiring')}</Badge>}
                      {k.writeEnabled && <Badge>{t('settings:apiKeys.writeBadge')}</Badge>}
                      {k.sendEnabled && <Badge>{t('settings:apiKeys.sendBadge')}</Badge>}
                      {k.deleteEnabled && <Badge>{t('settings:apiKeys.deleteBadge')}</Badge>}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {k.prefix ? `${k.prefix}••••  · ` : ''}
                      {t('settings:apiKeys.createdAt', { date: new Date(k.createdAt).toLocaleDateString(currentLocale()) })} · {st.text}
                      {k.lastUsedAt
                        ? ` · ${t('settings:apiKeys.lastUsedAt', { date: new Date(k.lastUsedAt).toLocaleDateString(currentLocale()) })}`
                        : ` · ${t('settings:apiKeys.neverUsed')}`}
                    </p>
                  </div>
                  {st.state !== 'revoked' && coQuyenTao && (
                    <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                      {(['write', 'send', 'delete'] as const).map((cap) => (
                        <label key={cap} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {t(`settings:apiKeys.${cap}Short`)}
                          <Switch
                            checked={k[`${cap}Enabled`]}
                            disabled={updateKey.isPending}
                            onCheckedChange={(v) => toggleCap(k.id, cap, v)}
                            aria-label={t(`settings:apiKeys.${cap}EnabledLabel`)}
                          />
                        </label>
                      ))}
                    </div>
                  )}
                  {st.state !== 'revoked' && coQuyenThuHoi && (
                    <Button variant="ghost" size="icon" onClick={() => remove(k.id, k.name)} aria-label={t('settings:apiKeys.revokeAria', { name: k.name })}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
